import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Box, Button, CardContent, CardHeader, makeStyles, Paper } from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity, EnumPrecioUnitario } from '../../../../api'
import {
  ConceptLayoutConfigurationModel,
  NewConceptPrice
} from '../../../../api/Argentia/endpoints/concepts/model/concepts'
import { EnumPeriodo } from '../../../../api/Argentia/enums/periodo'
import { EditablePriceModel } from '../../../../api/Argentia/model/editablePriceModel'
import {
  EnumRegionImpuestos,
  EnumTipoCaudal,
  EnumTipoPeaje,
  EnumTipoProductoCapacidad,
  EnumTipoUso
} from '../../../../api/oData/enums'
import { EnumPriceType } from '../../../../api/oData/enums/enumPriceType'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { EmptyContent } from '../../../../components/shared/emptyCardContent/emptyContent'
import { LoadingCard } from '../../../../components/shared/loadingCard/loadingCard'
import { enumToSelectOption, enumToSelectOptionArray } from '../../../../helpers'
import { useConfirmationDialog } from '../../../../hooks'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { getCustomerDomain } from '../../../../redux'
import { savePrice } from '../../../pricing/pricing.service'
import {
  buildConceptPriceModelRequest,
  createDefaultEditablePriceModel,
  mapConceptPricesForDataTable,
  mapConceptPriceToEditablePriceModel
} from '../../concepts.mapper'
import { ConceptPriceCardDataTable } from '../../concepts.models'
import { createConceptPrice, deleteConceptPrice, updateConceptPrice } from '../../concepts.service'
import { conceptPriceCardStyles } from './conceptPriceCard.styles'
import { ConceptPriceEditDialog } from './conceptPriceEditDialog'

interface ConceptPriceCardProps {
  isFetching?: boolean
  conceptPrice?: NewConceptPrice[] | null
  conceptId: number
  onAddConceptPrice?: (price: NewConceptPrice) => void
  onEditPrice?: (price: NewConceptPrice) => void
  onRefreshPage?: () => void
  configuration: ConceptLayoutConfigurationModel
  readOnly?: boolean
  raised?: boolean
  commodity: EnumCommodity
}

export const ConceptPriceCard: React.FC<ConceptPriceCardProps> = ({
  isFetching,
  conceptPrice,
  conceptId,
  onAddConceptPrice,
  onEditPrice,
  onRefreshPage,
  configuration,
  readOnly = false,
  raised = true,
  commodity
}) => {
  const [t] = useTranslation(['conceptsEdit', 'globalerisLibrary', 'enums', 'dataTable'])
  const { info, error } = useNotify()
  const useStyles = makeStyles(conceptPriceCardStyles)
  const classes = useStyles()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [isAddMode, setIsAddMode] = useState(false)
  const [selectedPriceForEdit, setSelectedPriceForEdit] = useState<EditablePriceModel | null>(null)
  const [selectedPriceType, setSelectedPriceType] = useState<EnumPriceType>(EnumPriceType.Formula)
  const [selectedCommodity, setSelectedCommodity] = useState<EnumCommodity>(commodity)
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false)
  // Store original values for edit mode comparison
  const [editingConceptPriceId, setEditingConceptPriceId] = useState<number | null>(null)
  const [originalDiscriminants, setOriginalDiscriminants] = useState<{
    toll?: number | null
    usage?: number | null
    flow?: number | null
    duration?: number | null
    taxRegion?: number | null
    period?: number | null
  } | null>(null)

  // Check if all lg* fields are false
  const allLgFalse = useMemo(() => {
    return (
      !configuration.lgToll &&
      !configuration.lgUsage &&
      !configuration.lgUnitPriceFlow &&
      !configuration.lgDuration &&
      !configuration.lgRegion &&
      !configuration.lgPeriod &&
      !configuration.lgProduct
    )
  }, [configuration])

  // For all lg* false: disable FAB if price already exists
  const isFabDisabled = useMemo(() => {
    if (allLgFalse) {
      return conceptPrice != null && conceptPrice.length > 0
    }
    return false
  }, [allLgFalse, conceptPrice])

  // #region Handlers Starts
  const onAddConceptPriceHandler = async () => {
    // Valores por defecto para nuevo precio
    const defaultPrice = createDefaultEditablePriceModel()

    setIsAddMode(true)
    setSelectedPriceForEdit(defaultPrice)
    setSelectedPriceType(EnumPriceType.Formula)
    setSelectedCommodity(commodity)
    setShowEditDialog(true)
    return true
  }

  const onEditPriceHandler = (row: ConceptPriceCardDataTable) => {
    const editablePrice = mapConceptPriceToEditablePriceModel(row)
    setIsAddMode(false)
    setSelectedPriceForEdit(editablePrice)
    setSelectedPriceType((row.price?.priceType as EnumPriceType) ?? EnumPriceType.Formula)
    setSelectedCommodity((row.price?.commodity as EnumCommodity) ?? commodity)
    // Store conceptPriceId and original discriminants for comparison
    setEditingConceptPriceId(row.id ?? null)
    setOriginalDiscriminants({
      toll: row.toll ?? null,
      usage: row.usage ?? null,
      flow: row.flow ?? null,
      duration: row.duration ?? null,
      taxRegion: row.taxRegion ?? null,
      period: row.period ?? null
    })
    setShowEditDialog(true)
  }

  const onDeletePriceHandler = async (row: ConceptPriceCardDataTable) => {
    await getConfirmation(ConfirmationDialogs.DELETE_CONCEPT_PRICE).then(async () => {
      if (!row.id) {
        error(t('conceptsEdit:notifications.priceDelete.error'))
        return
      }
      setIsUpdatingPrice(true)
      await deleteConceptPrice(row.id, '2.0')
        .then(response => {
          if (response.success) {
            info(t('conceptsEdit:notifications.priceDelete.success'))
            onRefreshPage && onRefreshPage()
          } else {
            error(t('conceptsEdit:notifications.priceDelete.error'))
          }
        })
        .finally(() => {
          setIsUpdatingPrice(false)
        })
    })
    closeConfirmation()
  }

  const handleCloseDummyPriceEdit = async (priceFields?: EditablePriceModel) => {
    if (priceFields) {
      setIsUpdatingPrice(true)
      if (isAddMode) {
        // Crear nuevo conceptPrice usando POST ConceptPrice
        const conceptPriceRequest = buildConceptPriceModelRequest(
          conceptId,
          priceFields,
          selectedPriceType,
          selectedCommodity,
          configuration
        )

        await createConceptPrice(conceptPriceRequest, '2.0')
          .then(response => {
            if (response.success) {
              info(t('conceptsEdit:notifications.priceCreate.success'))
              onRefreshPage && onRefreshPage()
            } else {
              error(t('conceptsEdit:notifications.priceCreate.error'))
            }
          })
          .finally(() => {
            setIsUpdatingPrice(false)
          })
      } else {
        // Editar precio existente
        await updateConceptPriceHandler(priceFields)
          .then(response => {
            if (response.success) {
              info(t('conceptsEdit:notifications.priceEdit.success'))
              onRefreshPage && onRefreshPage()
            } else {
              error(t('conceptsEdit:notifications.priceEdit.error'))
            }
          })
          .catch(() => {
            error(t('conceptsEdit:notifications.priceEdit.error'))
          })
          .finally(() => {
            setIsUpdatingPrice(false)
          })
      }
    }
    setShowEditDialog(false)
  }
  // #endregion Handlers Ends

  // #region DataTable Config Starts
  // DataTable Config with dynamic discriminant columns for layout 4
  const columns: DataTableColumn[] = useMemo(() => {
    const baseColumns: DataTableColumn[] = [
      {
        name: 'price.code',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.code'),
        type: DataType.TEXT,
        display: false
      },
      {
        name: 'price.description',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.description'),
        type: DataType.TEXT,
        display: false
      },
      {
        name: 'price.expression',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.expression'),
        type: DataType.TEXT
      },
      {
        name: 'price.precioUnitario',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.priceUnit'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOption(EnumPrecioUnitario)
      }
    ]

    const discriminantColumns: DataTableColumn[] = []

    if (configuration.lgToll) {
      discriminantColumns.push({
        name: 'toll',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.toll'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoPeaje, 'enums:EnumTipoPeaje')
      })
    }

    if (configuration.lgUsage) {
      discriminantColumns.push({
        name: 'usage',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.usage'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoUso, 'enums:EnumTipoUso')
      })
    }

    if (configuration.lgUnitPriceFlow) {
      discriminantColumns.push({
        name: 'flow',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.flow'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoCaudal, 'enums:EnumTipoCaudal')
      })
    }

    if (configuration.lgDuration) {
      discriminantColumns.push({
        name: 'duration',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.duration'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(
          EnumTipoProductoCapacidad,
          'enums:EnumTipoProductoCapacidad'
        )
      })
    }

    if (configuration.lgRegion) {
      discriminantColumns.push({
        name: 'taxRegion',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.region'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumRegionImpuestos, 'enums:EnumRegionImpuestos')
      })
    }

    if (configuration.lgPeriod) {
      discriminantColumns.push({
        name: 'period',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.period'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumPeriodo, 'enums:EnumPeriodo')
      })
    }

    return [...baseColumns, ...discriminantColumns]

    //return baseColumns
  }, [configuration, t])

  const tableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: readOnly ? false : !isFabDisabled, // Disable FAB in read-only mode or when all lg* false and price exists
    customInsertEditModal: true,
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: undefined,
    analytics: {
      customer: getCustomerDomain(),
      view: t('conceptsEdit:components.conceptPriceCard.dataTable.analytics')
    },
    onRowInsert: onAddConceptPriceHandler,
    rowCustomActions: readOnly
      ? [] // No actions in read-only mode
      : [
          {
            icon: <Edit fontSize="small" />,
            tooltip: t('conceptsEdit:components.conceptPriceCard.dataTable.actions.edit'),
            onClick: onEditPriceHandler,
            userPermission: UserPermission.EDIT
          },
          {
            icon: <Delete fontSize="small" />,
            tooltip: t('conceptsEdit:components.conceptPriceCard.dataTable.actions.delete'),
            onClick: onDeletePriceHandler,
            userPermission: UserPermission.EDIT
          }
        ]
  }

  // Mapear conceptPrice al formato que el DataTable espera
  const priceData: ConceptPriceCardDataTable[] = useMemo(
    () => mapConceptPricesForDataTable(conceptPrice ?? null),
    [conceptPrice]
  )
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const updateConceptPriceHandler = async (priceFields: EditablePriceModel) => {
    // Detectar qué cambió
    let priceUpdateSuccess = true
    let discriminantUpdateSuccess = true

    // Check if price fields changed (expression, precioUnitario, redondeo, priceCalculationType)
    const priceChanged =
      selectedPriceForEdit &&
      (selectedPriceForEdit.expression !== priceFields.expression ||
        selectedPriceForEdit.precioUnitario !== priceFields.precioUnitario ||
        selectedPriceForEdit.redondeo !== priceFields.redondeo ||
        selectedPriceForEdit.priceCalculationType !== priceFields.priceCalculationType)

    // Check if discriminants changed
    const discriminantsChanged =
      originalDiscriminants &&
      (originalDiscriminants.toll !== priceFields.toll ||
        originalDiscriminants.usage !== priceFields.usage ||
        originalDiscriminants.flow !== priceFields.flow ||
        originalDiscriminants.duration !== priceFields.duration ||
        originalDiscriminants.taxRegion !== priceFields.taxRegion ||
        originalDiscriminants.period !== priceFields.period)

    // Update price if changed
    if (priceChanged && priceFields.id) {
      const priceResponse = await savePrice(priceFields.id, priceFields)
      priceUpdateSuccess = priceResponse.success
    }

    // Update discriminants if changed
    if (discriminantsChanged && editingConceptPriceId) {
      const conceptPricePatch = {
        id: editingConceptPriceId,
        ...(priceFields.toll !== undefined && { peaje: priceFields.toll }),
        ...(priceFields.usage !== undefined && { uso: priceFields.usage }),
        ...(priceFields.flow !== undefined && { caudal: priceFields.flow }),
        ...(priceFields.duration !== undefined && { duracion: priceFields.duration }),
        ...(priceFields.taxRegion !== undefined && { regionImpuestos: priceFields.taxRegion }),
        ...(priceFields.period !== undefined && { periodo: priceFields.period })
      }

      const discriminantResponse = await updateConceptPrice(
        conceptId,
        editingConceptPriceId,
        conceptPricePatch,
        '2.0'
      )
      discriminantUpdateSuccess = discriminantResponse.success
    }

    return { success: priceUpdateSuccess && discriminantUpdateSuccess }
  }
  // #endregion Hooks Ends

  if (isFetching || isUpdatingPrice) {
    return <LoadingCard />
  }

  return (
    <>
      <Box marginTop={2} marginBottom={2}>
        <CardWithTransition show={true} raised={raised}>
          <Paper elevation={3}>
            <CardHeader
              title={t('conceptsEdit:components.conceptPriceCard.titles.cardTitle')}
              titleTypographyProps={{ variant: 'h2', color: 'secondary' }}
            />
            <CardContent
              className={
                isFabDisabled || readOnly ? classes.dialogContent : classes.dialogContentWithFab
              }
            >
              {!isFetching && !isUpdatingPrice && priceData.length === 0 ? (
                <EmptyContent
                  caption={t('conceptsEdit:components.conceptPriceCard.emptyContent.caption')}
                >
                  {!readOnly && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={onAddConceptPriceHandler}
                      disabled={isFabDisabled}
                    >
                      {t('conceptsEdit:components.conceptPriceCard.emptyContent.button')}
                    </Button>
                  )}
                </EmptyContent>
              ) : (
                <DataTable
                  data={!isFetching ? priceData : undefined}
                  columns={columns}
                  options={tableOptions}
                  userPermissions={readOnly ? [UserPermission.READ_ONLY] : [UserPermission.EDIT]}
                />
              )}
            </CardContent>
          </Paper>
        </CardWithTransition>
      </Box>

      {showEditDialog && selectedPriceForEdit && (
        <ConceptPriceEditDialog
          open={showEditDialog}
          onClose={handleCloseDummyPriceEdit}
          priceType={selectedPriceType}
          commodity={selectedCommodity}
          priceData={selectedPriceForEdit}
          isFetching={false}
          configuration={configuration}
          existingPrices={conceptPrice ?? []}
          isAddMode={isAddMode}
        />
      )}
    </>
  )
}
